<template>
  <div class="reporting-channels">

    <!-- Banner -->
    <div class="banner">
      <div class="logo-container">
        <div class="logo-left">
          <img src="../../assets/logo2.png" alt="School Logo" class="school-logo" />
        </div>
        <div class="banner-text">
          <h2>{{ bannerTitle }}</h2>
        </div>
        <div class="logo-right">
          <img src="../../assets/001.webp" alt="OMASC Logo" class="omasc-logo" />
        </div>
      </div>
    </div>

    <!-- Intro -->
    <div class="intro-section">
      <div class="intro-inner">
        <p class="intro-en" v-html="introText"></p>
        <p class="intro-ar" v-if="introTextAr" v-html="introTextAr"></p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading contacts…</p>
    </div>

    <!-- Contact Cards -->
    <div v-else class="contacts-grid">
      <div
        v-for="(contact, index) in contacts"
        :key="contact.id || index"
        class="contact-card"
        :style="{ animationDelay: index * 60 + 'ms' }"
      >
        <div class="card-image-section">
          <div class="image-wrapper">
            <img v-if="contact.photo" :src="contact.photo" :alt="contact.name" class="contact-image" />
            <div v-else class="image-placeholder">
              <span>{{ initials(contact.name) }}</span>
            </div>
          </div>
        </div>
        <div class="card-body">
          <h4 class="contact-name">{{ contact.name }}</h4>
          <p class="contact-title">{{ contact.title }}</p>
          <p class="contact-title-ar" v-if="contact.title_ar">{{ contact.title_ar }}</p>
          <div class="contact-details">
            <a v-if="contact.phone" :href="'tel:' + contact.phone" class="detail-link phone-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
              {{ contact.phone }}
            </a>
            <a v-if="contact.email" :href="'mailto:' + contact.email" class="detail-link email-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,12 2,6"/></svg>
              {{ contact.email }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════ Contact Form ═══════ -->
    <div class="contact-form-section">
      <div class="form-inner">
        <h3 class="form-title">Send Us a Message</h3>
        <p class="form-subtitle">أرسل لنا رسالة</p>

        <!-- Success -->
        <div v-if="formSuccess" class="form-success">
          <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2" width="40" height="40"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          <p>Thank you! Your message has been sent successfully.</p>
          <button class="btn-reset" @click="resetForm">Send Another</button>
        </div>

        <!-- Form -->
        <form v-else class="contact-form" @submit.prevent="submitForm">
          <div class="form-row">
            <div class="form-group">
              <label for="cf-name">Full Name *</label>
              <input id="cf-name" v-model="form.name" type="text" required placeholder="Your full name" />
            </div>
            <div class="form-group">
              <label for="cf-email">Email Address *</label>
              <input id="cf-email" v-model="form.email" type="email" required placeholder="your@email.com" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="cf-phone">Phone Number</label>
              <input id="cf-phone" v-model="form.phone" type="tel" placeholder="+971 XX XXXXXXX" />
            </div>
            <div class="form-group">
              <label for="cf-subject">Subject *</label>
              <input id="cf-subject" v-model="form.subject" type="text" required placeholder="Message subject" />
            </div>
          </div>
          <div class="form-group full-width">
            <label for="cf-message">Message *</label>
            <textarea id="cf-message" v-model="form.message" required rows="5" placeholder="Type your message here…"></textarea>
          </div>
          <div v-if="formError" class="form-error">
            <p>{{ formError }}</p>
          </div>
          <button type="submit" class="btn-submit" :disabled="formSubmitting">
            <span v-if="formSubmitting" class="btn-spinner"></span>
            {{ formSubmitting ? 'Sending…' : 'Send Message' }}
          </button>
        </form>
      </div>
    </div>

    <!-- Footer Banner -->
    <div class="footer-banner">
      <p class="footer-arabic">{{ footerTextAr }}</p>
      <p class="footer-number">{{ footerNumber }}</p>
    </div>

  </div>
</template>

<script>
import api from '@/services/api';
import { mediaUrl } from '@/services/media';

export default {
  name: 'ContactContent',
  data() {
    return {
      loading: true,
      bannerTitle: 'Reporting Channels - قنوات الإبلاغ',
      introText: '<p>How can a parent or student report bullying of any kind or report a digital safety breach?</p>',
      introTextAr: '',
      footerTextAr: 'للإبلاغ عن أي حالة للتعامل معها أمنياً يرجى الاتصال بـ',
      footerNumber: '900 988',
      contacts: [],
      // Form state
      form: { name: '', email: '', phone: '', subject: '', message: '' },
      formSubmitting: false,
      formSuccess: false,
      formError: '',
    }
  },

  async created() {
    try {
      const pageData = await api.getContactPage();
      const page = pageData?.page || pageData;
      if (page?.banner_title) this.bannerTitle = page.banner_title;
      if (page?.intro_text) this.introText = page.intro_text;
      if (page?.intro_text_ar) this.introTextAr = page.intro_text_ar;
      if (page?.footer_text_ar) this.footerTextAr = page.footer_text_ar;
      if (page?.footer_number) this.footerNumber = page.footer_number;

      const peopleFallback = (pageData?.contact_people || []).map(c => ({
        ...c, photo: mediaUrl(c.photo),
      }));

      let people = [];
      try {
        const pd = await api.getContactPeople();
        people = api.extractList(pd);
      } catch { people = peopleFallback; }

      if (people.length > 0) {
        this.contacts = people.map(c => ({ ...c, photo: mediaUrl(c.photo) }));
      }
    } catch (e) {
      console.warn('[Contact] API error:', e.message);
      try {
        const pd = await api.getContactPeople();
        this.contacts = api.extractList(pd).map(c => ({ ...c, photo: mediaUrl(c.photo) }));
      } catch (e2) {
        console.warn('[Contact] Fallback also failed:', e2.message);
      }
    } finally {
      this.loading = false;
    }
  },

  methods: {
    initials(name = '') {
      return name.split(' ').slice(0, 2).map(w => (w || '')[0]).filter(Boolean).join('').toUpperCase();
    },

    async submitForm() {
      this.formError = '';
      this.formSubmitting = true;
      try {
        await api.submitContactForm({
          name: this.form.name,
          email: this.form.email,
          phone: this.form.phone || '',
          subject: this.form.subject,
          message: this.form.message,
        });
        this.formSuccess = true;
      } catch (e) {
        console.warn('[Contact] Form submission error:', e.message);
        this.formError = 'Failed to send your message. Please try again or contact us directly.';
      } finally {
        this.formSubmitting = false;
      }
    },

    resetForm() {
      this.form = { name: '', email: '', phone: '', subject: '', message: '' };
      this.formSuccess = false;
      this.formError = '';
    }
  }
}
</script>

<style scoped>
/* ── Root ── */
.reporting-channels { max-width: 1000px; margin: 0 auto; background: #fff; color: #222; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; }

/* ── Banner ── */
.banner { background: linear-gradient(135deg, #C02130 0%, #A01A26 100%); padding: 24px 28px; box-shadow: 0 4px 12px rgba(192,33,48,0.15); }
.logo-container { display: flex; align-items: center; justify-content: space-between; gap: 20px; }
.school-logo, .omasc-logo { width: 90px; height: 90px; object-fit: contain; flex-shrink: 0; }
.banner-text { flex: 1; text-align: center; }
.banner-text h2 { color: #fff; font-size: clamp(18px, 3.5vw, 26px); font-weight: 700; margin: 0; }

/* ── Intro ── */
.intro-section { padding: 40px 32px; border-bottom: 4px solid #ff9800; background: linear-gradient(to bottom, #fafafa, #f5f5f5); }
.intro-inner { max-width: 750px; margin: 0 auto; text-align: center; }
.intro-en { color: #2c3e50; font-size: 18px; font-weight: 500; line-height: 1.8; margin: 0 0 12px; }
.intro-ar { color: #555; font-size: 16px; direction: rtl; line-height: 1.9; margin: 0; font-family: 'Arial', sans-serif; font-weight: 500; }

/* ── Loading ── */
.loading-state { display: flex; flex-direction: column; align-items: center; padding: 80px 20px; gap: 16px; color: #888; }
.spinner { width: 40px; height: 40px; border: 3px solid #eee; border-top-color: #C02130; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Contacts Grid ── */
.contacts-grid { padding: 40px 28px; display: grid; gap: 24px; }

/* ── Card ── */
.contact-card { display: grid; grid-template-columns: 140px 1fr; gap: 24px; background: #fff; border: 1px solid #e8e8e8; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.05); transition: all 0.3s; animation: slideIn 0.5s ease both; }
.contact-card:hover { box-shadow: 0 8px 24px rgba(0,0,0,0.12); transform: translateY(-2px); border-color: #ff9800; }
@keyframes slideIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

.card-image-section { display: flex; align-items: stretch; overflow: hidden; background: linear-gradient(135deg, #f5f5f5, #efefef); }
.image-wrapper { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; min-height: 160px; }
.contact-image { width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s; }
.contact-card:hover .contact-image { transform: scale(1.05); }
.image-placeholder { width: 100%; height: 100%; background: linear-gradient(135deg, #C02130, #ff9800); display: flex; align-items: center; justify-content: center; min-height: 160px; }
.image-placeholder span { color: #fff; font-size: 32px; font-weight: 800; }

.card-body { padding: 20px 24px; display: flex; flex-direction: column; justify-content: flex-start; min-width: 0; }
.contact-name { color: #1a1a1a; font-size: 19px; font-weight: 700; margin: 0 0 6px; }
.contact-title { color: #C02130; font-size: 14px; font-weight: 600; margin: 0 0 4px; }
.contact-title-ar { color: #777; font-size: 13px; direction: rtl; margin: 0 0 14px; font-family: 'Arial', sans-serif; }

.contact-details { display: flex; flex-direction: column; gap: 10px; margin-top: 8px; }
.detail-link { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; text-decoration: none; transition: all 0.2s; width: fit-content; padding: 6px 0; }
.detail-link svg { width: 16px; height: 16px; flex-shrink: 0; }
.phone-link { color: #2c3e50; }
.phone-link:hover { color: #C02130; padding-left: 4px; }
.email-link { color: #4A7BA7; word-break: break-all; }
.email-link:hover { color: #2c5282; padding-left: 4px; }

/* ══════════ Contact Form ══════════ */
.contact-form-section {
  padding: 48px 28px;
  background: linear-gradient(to bottom, #f8f8f8, #f2f2f2);
  border-top: 4px solid #C02130;
}
.form-inner { max-width: 680px; margin: 0 auto; }
.form-title { text-align: center; font-size: 26px; font-weight: 800; color: #1a1a1a; margin: 0 0 4px; }
.form-subtitle { text-align: center; font-size: 16px; color: #888; margin: 0 0 32px; font-family: 'Arial', sans-serif; direction: rtl; }

.contact-form { display: flex; flex-direction: column; gap: 20px; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.form-group { display: flex; flex-direction: column; gap: 6px; }
.form-group.full-width { grid-column: 1 / -1; }

.form-group label { font-size: 13px; font-weight: 600; color: #555; }
.form-group input, .form-group textarea {
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  font-size: 15px;
  font-family: inherit;
  transition: border-color 0.25s, box-shadow 0.25s;
  background: #fff;
  resize: vertical;
}
.form-group input:focus, .form-group textarea:focus {
  outline: none;
  border-color: #C02130;
  box-shadow: 0 0 0 3px rgba(192,33,48,0.1);
}

.btn-submit {
  align-self: center;
  padding: 14px 48px;
  background: #C02130;
  color: #fff;
  border: none;
  border-radius: 28px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.btn-submit:hover:not(:disabled) { background: #a01a26; transform: translateY(-2px); box-shadow: 0 6px 20px rgba(192,33,48,0.3); }
.btn-submit:disabled { opacity: 0.7; cursor: not-allowed; }

.btn-spinner { width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: spin 0.6s linear infinite; }

.form-error { background: #FFF3F3; border: 1px solid #f8d7da; border-radius: 10px; padding: 12px 16px; text-align: center; }
.form-error p { color: #C02130; font-size: 14px; margin: 0; }

.form-success { text-align: center; padding: 40px 20px; display: flex; flex-direction: column; align-items: center; gap: 16px; }
.form-success p { font-size: 17px; color: #16a34a; font-weight: 600; margin: 0; }
.btn-reset { padding: 10px 28px; background: transparent; border: 2px solid #C02130; color: #C02130; border-radius: 24px; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.25s; }
.btn-reset:hover { background: #C02130; color: #fff; }

/* ── Footer Banner ── */
.footer-banner { background: linear-gradient(135deg, #C02130, #A01A26); padding: 36px 28px; text-align: center; }
.footer-arabic { color: rgba(255,255,255,0.95); font-size: 16px; direction: rtl; margin: 0 0 12px; font-family: 'Arial', sans-serif; font-weight: 500; }
.footer-number { color: #fff; font-size: 40px; font-weight: 900; margin: 0; letter-spacing: 3px; font-family: 'Courier New', monospace; text-shadow: 0 3px 10px rgba(0,0,0,0.3); }

/* ── Responsive ── */
@media (max-width: 768px) {
  .contact-card { grid-template-columns: 1fr; gap: 16px; }
  .card-image-section { order: -1; }
  .image-wrapper, .image-placeholder { min-height: 200px; }
  .card-body { padding: 16px 20px; }
  .intro-section { padding: 32px 24px; }
  .contacts-grid { padding: 28px 20px; gap: 16px; }
  .form-row { grid-template-columns: 1fr; }
  .contact-form-section { padding: 36px 20px; }
}
@media (max-width: 480px) {
  .banner { padding: 16px 20px; }
  .logo-container { flex-direction: column; gap: 12px; }
  .school-logo, .omasc-logo { width: 70px; height: 70px; }
  .banner-text h2 { font-size: 16px; }
  .footer-number { font-size: 32px; letter-spacing: 2px; }
}
</style>
